import { Ban, ChevronRight, Snowflake } from "lucide-react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";

import { findGradient } from "../constants/cardGradients";
import type { CardAugmented } from "../constants/cardMocks.constants";
import { useOrgDesign } from "../hooks/useOrgDesign";
import { normalizeProduct, normalizeStatus, type CardStatus } from "../utils/cardHelpers";

type CardRowProps = {
  card: CardAugmented;
  onOpen: (cardId: string) => void;
};

const STATUS_VARIANT: Record<
  CardStatus,
  {
    variant: "success" | "warning" | "destructive" | "secondary" | "default";
    tKey: string;
  }
> = {
  ACTIVE: { variant: "success", tKey: "cards_status_active" },
  FROZEN: { variant: "secondary", tKey: "cards_status_frozen" },
  DISABLED: { variant: "destructive", tKey: "cards_status_disabled" },
  DELETED: { variant: "destructive", tKey: "cards_status_deleted" },
  PENDING_REVIEW: { variant: "warning", tKey: "cards_status_pending_review" },
};

const formatUSD = (raw: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw);

// Keep in sync with CardVisual.module.css and CardPreviewVisual.tsx.
const BRAND_GRADIENT = [
  "radial-gradient(ellipse 80% 60% at 80% 0%, rgba(255,255,255,0.18), transparent 60%)",
  "radial-gradient(ellipse 100% 80% at 0% 100%, rgba(255,255,255,0.10), transparent 60%)",
  "linear-gradient(135deg, var(--color-brand-700) 0%, var(--color-brand-900) 60%, var(--color-brand-950) 100%)",
].join(", ");

export const CardRow = ({ card, onOpen }: CardRowProps) => {
  const { t } = useTranslations("cards");
  const design = useOrgDesign();

  const customMiniGradient = design?.gradient_id
    ? [
        "radial-gradient(ellipse 80% 60% at 80% 0%, rgba(255,255,255,0.18), transparent 60%)",
        "radial-gradient(ellipse 100% 80% at 0% 100%, rgba(255,255,255,0.10), transparent 60%)",
        findGradient(design.gradient_id).css,
      ].join(", ")
    : null;

  const status = normalizeStatus(card.status || card.card_status);
  const product = normalizeProduct(card.card_type || card.product);
  const statusMeta = STATUS_VARIANT[status];
  const pocket = card.pocketRef;
  const available = card.balanceMock;
  const last4 = card.last4 || card.last_4 || "0000";
  const holder = card.holder || card.card_holder_name || card.name || "—";
  const alias = card.alias?.trim() || `•••• ${last4}`;

  return (
    <button
      type="button"
      onClick={() => onOpen(card.id)}
      className="flex w-full items-center gap-4 rounded-[var(--radius-lg)] border border-border bg-card p-3.5 text-left transition-all hover:border-ring/40 hover:shadow-[var(--shadow-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div
        className="relative h-14 w-[88px] shrink-0 overflow-hidden rounded-[var(--radius-md)] text-white"
        style={{ background: customMiniGradient ?? BRAND_GRADIENT }}
      >
        <div className="relative flex h-full flex-col justify-between p-2">
          <div className="font-mono text-[8px] uppercase tracking-wider text-white/65">
            {product === "VIRTUAL" ? t("cards_type_virtual_short") : t("cards_type_fisica_short")}
          </div>
          <div className="font-mono text-[10px] text-white/95">··{last4}</div>
        </div>
        {card.disabled ? (
          <div className="absolute inset-0 flex items-center justify-center bg-destructive/20 backdrop-blur-[1px]">
            <span
              aria-label={t("cards_status_disabled")}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-md ring-1 ring-white/25"
            >
              <Ban className="h-3.5 w-3.5" />
            </span>
          </div>
        ) : card.frozen ? (
          <div className="absolute inset-0 flex items-center justify-center bg-sky-500/20 backdrop-blur-[1px]">
            <span
              aria-label={t("cards_status_frozen")}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-600 text-white shadow-md ring-1 ring-white/25"
            >
              <Snowflake className="h-3.5 w-3.5" />
            </span>
          </div>
        ) : null}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <div className="truncate text-sm font-medium text-foreground">{alias}</div>
          <ColocaBadge variant={statusMeta.variant}>{t(statusMeta.tKey as never)}</ColocaBadge>
          {product === "PHYSICAL" && <ColocaBadge variant="default">{t("product_physical")}</ColocaBadge>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {pocket && (
            <>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: pocket.color }}
                />
                <span className="font-medium text-foreground">
                  {t("cards_balance_pocket_prefix")}
                  {pocket.name}
                </span>
              </span>
              <span>·</span>
            </>
          )}
          <span className="truncate">{holder}</span>
          <span>·</span>
          <span className="font-mono font-medium tracking-wider">{last4}</span>
        </div>
      </div>

      <div className="hidden w-[160px] shrink-0 md:block">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {pocket ? t("cards_label_balance_pocket") : t("cards_label_balance_card")}
        </div>
        <div className="text-sm font-semibold tabular-nums text-foreground">{formatUSD(available)}</div>
      </div>

      <div className="hidden w-[120px] shrink-0 text-right md:block">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("cards_label_month_spend")}
        </div>
        <div className="font-mono text-sm tabular-nums text-foreground">{formatUSD(card.monthSpend)}</div>
      </div>

      <div className="shrink-0 text-right md:hidden">
        <div className="text-sm font-semibold tabular-nums text-foreground">{formatUSD(available)}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {pocket ? t("cards_label_balance_pocket") : t("cards_label_balance_card")}
        </div>
      </div>

      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </button>
  );
};
